import type { Zero } from "@rocicorp/zero";
import { ZeroProvider } from "@rocicorp/zero/react";
import * as Sentry from "@sentry/react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import type { Context } from "../../shared/authorization";
import { schema } from "../../shared/generatedDrizzleSchema";
import { sharedMutators } from "../../shared/mutators";
import { emitErrorToast } from "../lib/errorToast";
import { getTraceparent } from "../lib/otel";
import { apiUrl, zeroSyncUrl } from "../lib/tenantOrigin";
import {
  emitSpan,
  initTracing,
  sessionId,
  trackConnectionState,
  trackMutation,
} from "../lib/tracing";
import { classifyZeroConnectionError } from "../lib/zeroConnectionError";
import { zeroLogger } from "../lib/zeroLogger";
import { classifyZeroResult, type ZeroResult } from "../lib/zeroResultError";
import { reportZeroDefinitionNotFound } from "../lib/reportZeroDefinitionNotFound";

function initZero(z: Zero) {
  patchMutate(z);
  trackConnectionState(z);
  reportConnectionErrors(z);
  signInAgainWhenAuthLapses(z);
}

// needs-auth is terminal: Zero's run loop parks there until connect() is
// called, so a session that lapses under an open tab freezes it with no sign
// that anything is wrong. Reloading re-runs the session bootstrap, which sends
// a signed-out user to the IdP. Once per page load — if the bootstrap disagreed
// with Zero about the session, reloading on every transition would never stop.
function signInAgainWhenAuthLapses(z: Zero) {
  let reloaded = false;
  z.connection.state.subscribe((state) => {
    if (state.name !== "needs-auth" || reloaded) return;
    reloaded = true;
    window.location.reload();
  });
}

// Following https://zero.rocicorp.dev/docs/connection#error
function reportConnectionErrors(z: Zero) {
  let inErrorState = false;
  z.connection.state.subscribe((state) => {
    if (state.name !== "error") {
      inErrorState = false;
      return;
    }
    if (inErrorState) return;
    inErrorState = true;
    const decision = classifyZeroConnectionError(state.reason);
    if (decision.kind === "ignore") return;
    Sentry.withScope((scope) => {
      scope.setTag("zero.connection.state", state.name);
      scope.setExtra("zero.connection.reason", state.reason);
      scope.setFingerprint(decision.fingerprint);
      Sentry.captureException(
        new Error(`Zero connection error: ${state.reason}`)
      );
    });
  });
}

function patchMutate(z: Zero) {
  const origMutate = z.mutate;
  const handleError = (r: ZeroResult, scope: string) => {
    if (scope === "server") reportZeroDefinitionNotFound(r.error);
    const action = classifyZeroResult(r);
    if (action.kind === "ignore") return;
    if (action.message === undefined && scope !== "server") return;
    emitErrorToast({
      icon: "circle-off",
      message: action.message ?? "Something went wrong. Please try again.",
      buttonLabel: "Dismiss",
    });
  };
  (z as unknown as Record<string, unknown>).mutate = Object.assign(
    // oxlint-disable-next-line typescript/no-explicit-any -- patching Zero's callable mutate at init
    (mr: any) => {
      const start = performance.now();
      const name: string = mr?.mutator?.mutatorName ?? "unknown";
      const result = origMutate(mr);
      result.client.then((r: ZeroResult) => {
        handleError(r, "client");
        trackMutation(name, start, "client", r);
      });
      result.server.then((r: ZeroResult) => {
        handleError(r, "server");
        trackMutation(name, start, "server", r);
      });
      return result;
    },
    origMutate
  );
}

export function ZeroSetup({
  user,
  children,
}: {
  user: { id: string; tenantId: string };
  children: ReactNode;
}) {
  const [zeroBootStartOffset] = useState(() => performance.now());
  const context: Context = useMemo(
    () => ({ userId: user.id, tenantId: user.tenantId }),
    [user.id, user.tenantId]
  );

  // The view syncer reaches the API over an internal address that names no
  // tenant, so it can't infer the tenant from the request host the way a direct
  // browser call to the app origin does. Name it explicitly; the API still
  // authorizes it against the session cookie's own tenant.
  const zeroHeaders = useMemo(
    () => ({ "x-session-id": sessionId, "x-tenant-id": user.tenantId }),
    [user.tenantId]
  );

  useEffect(() => {
    initTracing(apiUrl(), user.tenantId);
    emitSpan(
      "ui.zero.boot",
      {
        "zero.boot.start_offset_ms": Math.round(zeroBootStartOffset),
      },
      undefined,
      performance.timeOrigin + zeroBootStartOffset
    );
  }, [user.tenantId, zeroBootStartOffset]);

  return (
    <ZeroProvider
      userID={user.id}
      cacheURL={zeroSyncUrl()}
      kvStore="mem"
      schema={schema}
      context={context}
      mutators={sharedMutators}
      init={initZero}
      queryHeaders={zeroHeaders}
      mutateHeaders={zeroHeaders}
      getTraceparent={getTraceparent}
      {...zeroLogger}
    >
      {children}
    </ZeroProvider>
  );
}
